'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { Playlist, Song } from '@/types/types';
import { useUser } from './userContext';


import { useSession } from './SessionContext';
import { resolvePlaylistImage } from '@/utils/imageUtils';
import { getReadableSongMetadata } from '@/utils/songMetadata';
import { useQueryClient } from '@tanstack/react-query';
import { playlistKeys } from '@/queries/playlistKeys';
interface CurrentPlaylistContextType {
    currentPlaylist: Playlist | null;
    setCurrentPlaylist: (playlist: Playlist | null) => void;
    addedSongsMap: Record<string, Song[]>;
    addSongToPlaylist: (playlistId: string, song: Song) => void;
    removeSongFromPlaylist: (playlistId: string, songId: string) => void;
    clearPlaylistSongs: (playlistId: string) => void;
    playPlaylist: (playlist: Playlist) => Promise<Playlist>;
    refreshCurrentPlaylist: () => Promise<void>;
    isPlaylistExpanded: boolean;
    setIsPlaylistExpanded: (expanded: boolean) => void;
    isAddingSong: boolean;
    setIsAddingSong: (adding: boolean) => void;

    getPlaylistSongs: (playlistId: string) => Promise<Song[]>;
    updatePlaylist: (playlistId: string, updatedPlaylist: Playlist) => void;
    isFetchingSongs: boolean;
    fetchPlaylistWithSongs: (playlistId: string, forceRefresh?: boolean) => Promise<Playlist | null>;

    isPlaylistOwner: (playlist: Playlist | null) => boolean;
    isCurrentPlaylistOwner: () => boolean;
    canModifyPlaylist: (playlist: Playlist | null) => boolean;
    canViewPlaylist: (playlist: Playlist | null) => boolean;

    // ✅ ADDED: New improved properties
    error: string | null;
    clearError: () => void;
    loadingStates: {
        fetchingPlaylist: boolean;
        addingSong: boolean;
        removingSong: boolean;
        clearingSongs: boolean;
    };
    invalidateCache: (playlistId?: string) => void;
    fetchVenueDetails: (venueId: string) => Promise<any>;
    getVenueName: (playlistData: any) => Promise<string>;
    prefetchVenues: (playlists: Playlist[]) => Promise<void>; // ✅ ADDED: Bulk venue pre-fetching
}

const CurrentPlaylistContext = createContext<CurrentPlaylistContextType | undefined>(undefined);


interface CurrentPlaylistProviderProps {
    children: ReactNode;
}

const orderStaticPlaylistSongs = (songs: any[]): any[] =>
    songs
        .map((song, originalIndex) => ({ song, originalIndex }))
        .sort((left, right) => {
            const leftSequence = Number(left.song?.sequence);
            const rightSequence = Number(right.song?.sequence);
            const leftHasSequence = Number.isFinite(leftSequence);
            const rightHasSequence = Number.isFinite(rightSequence);

            if (leftHasSequence && rightHasSequence && leftSequence !== rightSequence) {
                return leftSequence - rightSequence;
            }
            if (leftHasSequence !== rightHasSequence) {
                return leftHasSequence ? -1 : 1;
            }
            return left.originalIndex - right.originalIndex;
        })
        .map(({ song }) => song);

export const CurrentPlaylistProvider: React.FC<CurrentPlaylistProviderProps> = ({ children }) => {
    const { user, authToken } = useUser();
    const queryClient = useQueryClient();
    const userStorageId = user?.id ? String(user.id) : null;
    const addedSongsStorageKey = userStorageId ? `playlist_added_songs:${userStorageId}` : null;
    const currentPlaylistStorageKey = userStorageId ? `currentPlaylist:${userStorageId}` : null;

    const [currentPlaylist, setCurrentPlaylistState] = useState<Playlist | null>(null);
    const [addedSongsMap, setAddedSongsMap] = useState<Record<string, Song[]>>({});
    const [isPlaylistExpanded, setIsPlaylistExpanded] = useState(false);
    const [isAddingSong, setIsAddingSong] = useState(false);
    const [isFetchingSongs, setIsFetchingSongs] = useState(false);
    const { sessionActive, currentPlaylist: sessionPlaylist, sessionId } = useSession();
    // ✅ ADDED: New state declarations
    const [venueCache, setVenueCache] = useState<Record<string, string>>({});
    const [loadingStates, setLoadingStates] = useState({
        fetchingPlaylist: false,
        addingSong: false,
        removingSong: false,
        clearingSongs: false
    });
    const [error, setError] = useState<string | null>(null);
    const currentPlaylistRef = useRef<Playlist | null>(null);
    const manualSessionOverrideIdRef = useRef<string | null>(null);
    const normalizedSessionId = String(sessionId || "").trim();
    const normalizedSessionPlaylistId = String(sessionPlaylist?.id || "").trim();

    useEffect(() => {
        currentPlaylistRef.current = currentPlaylist;
    }, [currentPlaylist]);

    useEffect(() => {
        if (!sessionActive || !normalizedSessionId) {
            manualSessionOverrideIdRef.current = null;
        }
    }, [normalizedSessionId, sessionActive]);

    const applyCurrentPlaylistSelection = useCallback((
        playlist: Playlist | null,
        source: "manual" | "session" | "restore" = "manual"
    ) => {
        const nextPlaylistId = String(playlist?.id || "").trim();
        const hasActiveSessionContext = Boolean(
            sessionActive &&
            normalizedSessionId &&
            normalizedSessionPlaylistId
        );

        if (source === "session") {
            manualSessionOverrideIdRef.current = null;
            setCurrentPlaylistState(playlist);
            return;
        }

        if (!playlist) {
            manualSessionOverrideIdRef.current = null;
            setCurrentPlaylistState(null);
            return;
        }

        if (
            hasActiveSessionContext &&
            nextPlaylistId &&
            nextPlaylistId !== normalizedSessionPlaylistId
        ) {
            manualSessionOverrideIdRef.current = normalizedSessionId;
        } else {
            manualSessionOverrideIdRef.current = null;
        }

        setCurrentPlaylistState(playlist);
    }, [normalizedSessionId, normalizedSessionPlaylistId, sessionActive]);

    const setCurrentPlaylist = useCallback((playlist: Playlist | null) => {
        applyCurrentPlaylistSelection(playlist, "manual");
    }, [applyCurrentPlaylistSelection]);

    const getValidToken = useCallback((): string | null => {
        return authToken || localStorage.getItem("authToken");
    }, [authToken]);

    // ✅ ADDED: Retry function
    const fetchWithRetry = useCallback(async (url: string, options: RequestInit, retries = 3): Promise<Response> => {
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                const status = response.status;
                if (status === 401 || status === 403) {
                    throw new Error(`HTTP ${status}`);
                }
                throw new Error(`HTTP ${status}`);
            }
            return response;
        } catch (error) {
            const message = error instanceof Error ? error.message : "";
            const status = Number(message.replace("HTTP ", "")) || 0;
            if (status === 401 || status === 403) {
                throw error;
            }
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retries)));
                return fetchWithRetry(url, options, retries - 1);
            }
            throw error;
        }
    }, []);

    // ✅ ADDED: Debounced persistence
    const debouncedPersist = useCallback(
        (songsMap: Record<string, Song[]>) => {
            if (!addedSongsStorageKey) return;
            localStorage.setItem(addedSongsStorageKey, JSON.stringify(songsMap));
        },
        [addedSongsStorageKey]
    );

    const isPlaylistOwner = useCallback((playlist: Playlist | null): boolean => {
        if (!user || !playlist) return false;

        if (typeof playlist.is_owner === "boolean") {
            return playlist.is_owner;
        }

        const ownerCandidates = [
            playlist.owner_id,
            playlist.user_id,
            playlist.created_by,
        ].map((value) => String(value || "").trim()).filter(Boolean);

        const currentUserIds = [
            user.id,
            (user as any).user_id,
            (user as any).sub,
            (user as any).email,
        ].map((value) => String(value || "").trim()).filter(Boolean);

        const isOwner = ownerCandidates.length > 0
            ? ownerCandidates.some((ownerId) => currentUserIds.includes(ownerId))
            : playlist.access !== "Public";

        console.log("🔍 Ownership check - user owns all returned playlists:", {
            userId: user.id,
            playlistId: playlist?.id,
            playlistTitle: playlist?.title,
            isOwner
        });

        return isOwner;
    }, [user]);

    const isCurrentPlaylistOwner = useCallback((): boolean => {
        return isPlaylistOwner(currentPlaylist);
    }, [currentPlaylist, isPlaylistOwner]);

    // ✅ FIXED: Permission check matching your component's behavior
    const canModifyPlaylist = useCallback((playlist: Playlist | null): boolean => {
        if (!playlist || !user) return false;

        const isOwner = isPlaylistOwner(playlist);
        const isAdmin = user?.role === 'admin';
        const isVenueManager = user?.subscriptionPlan === 'venue';

        return isOwner || isAdmin || isVenueManager;
    }, [isPlaylistOwner, user]);

    // ✅ FIXED: View permissions matching your access type
    const canViewPlaylist = useCallback((playlist: Playlist | null): boolean => {
        if (!playlist) return false;

        // Public playlists are viewable by anyone
        if (playlist.access === 'Public') return true;

        // Private playlists only by owner/admin
        if (playlist.access === 'Private') {
            return canModifyPlaylist(playlist);
        }

        return true;
    }, [canModifyPlaylist]);

    // ✅ ENHANCED: Venue fetching with better handling of empty venue names
    const fetchVenueDetails = useCallback(async (venueId: string): Promise<any> => {
        if (!venueId || venueId === 'undefined' || venueId.startsWith('ven_') === false) {
            console.warn("❌ Invalid venue ID provided:", venueId);
            return null;
        }

        // Check cache first
        if (venueCache[venueId]) {
            console.log("🏢 Using cached venue name:", venueCache[venueId]);
            return { name: venueCache[venueId], id: venueId };
        }

        try {
            const token = getValidToken();
            if (!token) {
                console.warn("❌ No authentication token available");
                return null;
            }

            console.log("🏢 Fetching venue details for:", venueId);

            const response = await fetchWithRetry(`/api/v1/venues/${venueId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }, 2);

            if (!response.ok) {
                if (response.status === 404) {
                    console.warn(`❌ Venue not found: ${venueId}`);
                    // Cache the fallback result to prevent repeated failed lookups
                    const fallbackName = user?.name ? `${user.name}'s Venue` : "My Venue";
                    setVenueCache(prev => ({
                        ...prev,
                        [venueId]: fallbackName
                    }));
                    return { name: fallbackName, id: venueId };
                }
                console.warn(`⚠️ Failed to fetch venue ${venueId}: ${response.status}`);
                return null;
            }

            const venueData = await response.json();

            // ✅ ENHANCED: Handle empty or invalid venue names
            if (!venueData || !venueData.name || venueData.name.trim() === "") {
                console.warn("❌ Invalid or empty venue data received:", venueData);
                const fallbackName = user?.name ? `${user.name}'s Venue` : "My Venue";
                setVenueCache(prev => ({
                    ...prev,
                    [venueId]: fallbackName
                }));
                return { name: fallbackName, id: venueId };
            }

            // Cache the venue name
            setVenueCache(prev => ({
                ...prev,
                [venueId]: venueData.name
            }));

            console.log("✅ Venue details fetched:", {
                id: venueData.id,
                name: venueData.name,
                hasLogo: !!venueData.logo_url
            });
            return venueData;
        } catch (error) {
            console.error("❌ Error fetching venue details:", error);
            // Cache fallback to prevent repeated failed lookups
            const fallbackName = user?.name ? `${user.name}'s Venue` : "My Venue";
            setVenueCache(prev => ({
                ...prev,
                [venueId]: fallbackName
            }));
            return { name: fallbackName, id: venueId };
        }
    }, [getValidToken, venueCache, fetchWithRetry, user]);

    // ✅ ENHANCED: Comprehensive venue name resolution with empty string handling
    const getVenueName = useCallback(async (playlistData: any): Promise<string> => {
        if (!playlistData) {
            console.warn("❌ No playlist data provided to getVenueName");
            return user?.name ? `${user.name}'s Venue` : "My Venue";
        }

        console.log("🔍 Resolving venue name for playlist:", {
            playlistId: playlistData.id,
            playlistTitle: playlistData.title || playlistData.name,
            hasVenueId: !!playlistData.venue_id,
            directVenueName: playlistData.venue_name || playlistData.venueName,
            venueNameField: playlistData.venueName, // ✅ ADDED: Check specific field
            apiVenue: playlistData.apiVenue,
            apiVenueName: playlistData.apiVenueName
        });

        // 1. Check for direct venue name in playlist data (highest priority)
        // ✅ ENHANCED: Handle empty strings and "null" values
        const directVenueName = playlistData.venue_name || playlistData.venueName || playlistData.apiVenueName;
        if (directVenueName &&
            directVenueName !== "Unknown Venue" &&
            directVenueName !== "null" &&
            directVenueName.trim() !== "") {
            console.log("🏢 Using direct venue name from playlist:", directVenueName);
            return directVenueName;
        }

        // 2. Check for nested venue object
        if (playlistData.venue?.name &&
            playlistData.venue.name !== "Unknown Venue" &&
            playlistData.venue.name.trim() !== "") {
            console.log("🏢 Using nested venue name:", playlistData.venue.name);
            return playlistData.venue.name;
        }

        // 3. If we have a venue_id, fetch the venue details
        const venueId = playlistData.venue_id || playlistData.apiVenue_id || playlistData.venueId;
        if (venueId && venueId !== 'undefined' && venueId.startsWith('ven_')) {
            console.log("🔍 Fetching venue details for ID:", venueId);
            try {
                const venueDetails = await fetchVenueDetails(venueId);
                if (venueDetails?.name &&
                    venueDetails.name !== "Unknown Venue" &&
                    venueDetails.name.trim() !== "") {
                    console.log("🏢 Using venue name from API:", venueDetails.name);
                    return venueDetails.name;
                } else {
                    console.warn("⚠️ Venue API returned empty or invalid name:", venueDetails);
                }
            } catch (error) {
                console.error("❌ Failed to fetch venue name:", error);
            }
        } else {
            console.warn("⚠️ Invalid or missing venue ID:", venueId);
        }

        // 4. Check for sample venue or fallback data
        if (playlistData.sampleVenue?.name &&
            playlistData.sampleVenue.name.trim() !== "") {
            console.log("🏢 Using sample venue name:", playlistData.sampleVenue.name);
            return playlistData.sampleVenue.name;
        }

        // 5. Use playlist title as fallback (if it's venue-like)
        if (playlistData.title && playlistData.title.length < 30) {
            console.log("🏢 Using playlist title as venue fallback:", playlistData.title);
            return `${playlistData.title} Venue`;
        }

        // 6. Use user's name as fallback
        if (user?.name) {
            console.log("👤 Using user name as venue fallback:", user.name);
            return `${user.name}'s Venue`;
        }

        // 7. Final fallback
        console.warn("⚠️ No venue name found, using generic fallback");
        return "My Venue";
    }, [fetchVenueDetails, user]);

    // ✅ ADDED: Bulk venue pre-fetching for multiple playlists
    const prefetchVenues = useCallback(async (playlists: Playlist[]) => {
        const venueIds = playlists
            .map(p => p.venue_id)
            .filter(id => id && id.startsWith('ven_') && !venueCache[id])
            .filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates

        if (venueIds.length === 0) {
            console.log("🏢 No new venues to pre-fetch");
            return;
        }

        console.log("🔍 Pre-fetching venues:", venueIds);

        const venuePromises = venueIds.map(venueId => fetchVenueDetails(venueId));
        await Promise.allSettled(venuePromises);

        console.log("✅ Venue pre-fetching completed for", venueIds.length, "venues");
    }, [fetchVenueDetails, venueCache]);

    const mapSongFromApi = useCallback((song: any): Song | null => {
        if (!song || typeof song !== "object") return null;

        const id = song.id || song.song_id;
        if (!id) return null;

        const url = song.url || song.link || song.stream_url || song.source_url || "";
        const platform = song.platform || song.stream_domain || song.stream || 'youtube';
        const readableMetadata = getReadableSongMetadata(
            song.title || song.name || "Untitled",
            song.artist || song.singer || "Unknown Artist",
            platform
        );

        return {
            id: String(id),
            song_id: String(song.song_id || song.id || id),
            title: readableMetadata.title,
            artist: readableMetadata.artist,
            genre: song.genre || "",
            url,
            image: song.image || song.thumb_url || song.logo_url || "/default-song.png",
            votes: song.votes || song.vote_count || 0,
            hot: Boolean(song.hot),
            platform,
            duration: song.duration || 0,
            audioSrc: song.audioSrc || url,
            link: song.link || url,
            addition_count: Number(song.addition_count || 0),
            sequence: Number.isFinite(Number(song.sequence)) ? Number(song.sequence) : undefined,
            created_at: song.created_at,
            updated_at: song.updated_at,
            suggesters: Array.isArray(song.suggesters) ? song.suggesters : []
        };
    }, []);

    // ✅ ENHANCED: Song mapping with caching and retry
    const getPlaylistSongs = useCallback(async (
        playlistId: string,
        suppressExpectedError = false,
    ): Promise<Song[]> => {
        if (!playlistId) {
            console.warn("❌ No playlist ID provided to getPlaylistSongs");
            return [];
        }

        setIsFetchingSongs(true);
        if (!suppressExpectedError) setError(null);

        try {
            return await queryClient.fetchQuery({
                queryKey: playlistKeys.songs(playlistId),
                staleTime: 30_000,
                queryFn: async () => {
                    const token = getValidToken();
                    if (!token) return [];

                    const response = await fetchWithRetry(`/api/v1/songs/playlists/${playlistId}/songs`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }, 2);

                    if (!response.ok) {
                        throw new Error(`Failed to fetch songs: ${response.status}`);
                    }

                    const songsData = await response.json();

                    return Array.isArray(songsData)
                        ? orderStaticPlaylistSongs(songsData)
                            .map(mapSongFromApi)
                            .filter((song): song is Song => Boolean(song))
                        : [];
                },
            });
        } catch (error) {
            if (!suppressExpectedError) {
                console.error("❌ Error fetching playlist songs:", error);
                setError(error instanceof Error ? error.message : 'Failed to fetch songs');
            }
            throw error;
        } finally {
            setIsFetchingSongs(false);
        }
    }, [getValidToken, fetchWithRetry, mapSongFromApi, queryClient]);

    // ✅ ENHANCED: fetchPlaylistWithSongs with caching, retry, and empty venue name handling
    const fetchPlaylistWithSongs = useCallback(async (
        playlistId: string,
        forceRefresh = false
    ): Promise<Playlist | null> => {
        if (!playlistId) return null;

        if (forceRefresh) {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) }),
                queryClient.invalidateQueries({ queryKey: playlistKeys.songs(playlistId) }),
            ]);
        }

        try {
            return await queryClient.fetchQuery({
                queryKey: playlistKeys.detail(playlistId),
                staleTime: 30_000,
                queryFn: async () => {
                    setLoadingStates(prev => ({ ...prev, fetchingPlaylist: true }));
                    setError(null);
                    try {
            const token = getValidToken();
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const playlistEndpoints = token
                ? [`/api/v1/playlists/${playlistId}`, `/api/v1/playlists/${playlistId}/with-songs`]
                : [`/api/v1/playlists/${playlistId}/with-songs`, `/api/v1/playlists/${playlistId}`];

            console.log("🎵 Fetching complete playlist data:", playlistId);

            let playlistData: any = null;
            let lastPlaylistError: unknown = null;

            for (const endpoint of playlistEndpoints) {
                try {
                    const response = await fetchWithRetry(endpoint, { headers }, 1);
                    playlistData = await response.json();
                    if (playlistData) break;
                } catch (playlistError) {
                    lastPlaylistError = playlistError;
                }
            }

            if (!playlistData) {
                throw lastPlaylistError instanceof Error
                    ? lastPlaylistError
                    : new Error("Failed to fetch playlist");
            }

            let songs: Song[] = Array.isArray(playlistData?.songs)
                ? orderStaticPlaylistSongs(playlistData.songs)
                    .map(mapSongFromApi)
                    .filter((song): song is Song => Boolean(song))
                : [];

            // The playlist payload can contain a non-empty but stale embedded song list.
            // For authenticated opens, prefer the canonical songs endpoint so edits to
            // the persisted sequence are reflected outside an active session as well.
            if (token) {
                try {
                    const canonicalSongs = await getPlaylistSongs(playlistId, true);
                    songs = canonicalSongs;
                } catch {
                    // The playlist payload is a valid fallback; avoid surfacing an
                    // expected canonical-songs permission/network failure as an error.
                }
            }

            // Resolve venue name
            let venueName = "Unknown Venue";
            try {
                venueName = await getVenueName(playlistData);
                if (playlistData.venueName === "" || playlistData.venue_name === "") {
                    console.log("🔄 Detected empty venue name in API response, using resolved name:", venueName);
                }
            } catch (venueError) {
                console.error("❌ Error resolving venue name, using fallback:", venueError);
                venueName = user?.name ? `${user.name}'s Venue` : "My Venue";
            }

            console.log("🏢 Final venue name resolved:", {
                playlistId: playlistData.id,
                originalVenueName: playlistData.venueName,
                resolvedVenueName: venueName,
                venueId: playlistData.venue_id,
                success: venueName !== "Unknown Venue" && venueName.trim() !== ""
            });

            // ✅ CRITICAL FIX: Use resolveImage with cache busting
            const rawAccess = String(playlistData.type || playlistData.access || "Public").toLowerCase();
            const access: "Public" | "Private" = rawAccess === "private" ? "Private" : "Public";

            const playlist: Playlist = {
                id: playlistData.id || playlistId,
                title: playlistData.name || playlistData.title || "Untitled Playlist",
                creator: playlistData.creator || user?.name || "Unknown Creator",
                description: playlistData.description || "",
                location: String(playlistData.collection_country || playlistData.location || "US").toUpperCase(),
                access,
                genre: Array.isArray(playlistData.genre) ? playlistData.genre : [playlistData.category || playlistData.genre?.[0] || "Other"],
                image: resolvePlaylistImage(
                    playlistData.logo_url || playlistData.thumb_url || playlistData.image,
                    true,  // ← Forces ?v=timestamp → browser always loads fresh image
                    String(playlistData.id || playlistId || "")
                ),
                venueName: venueName,
                likes: playlistData.likes || 0,
                comments:
                    playlistData.comments ||
                    playlistData.comment_count ||
                    playlistData.comments_count ||
                    playlistData.commentCount ||
                    0,
                comment_count:
                    playlistData.comment_count ||
                    playlistData.comments_count ||
                    playlistData.commentCount ||
                    playlistData.comments ||
                    0,
                comments_count:
                    playlistData.comments_count ||
                    playlistData.comment_count ||
                    playlistData.commentCount ||
                    playlistData.comments ||
                    0,
                commentCount:
                    playlistData.commentCount ||
                    playlistData.comment_count ||
                    playlistData.comments_count ||
                    playlistData.comments ||
                    0,
                ratings: playlistData.ratings || 0,
                songs: songs,
                venue_id: playlistData.venue_id,
                owner_id: playlistData.owner_id,
                user_id: playlistData.user_id,
                created_by: playlistData.created_by,
                is_owner: typeof playlistData.is_owner === "boolean"
                    ? playlistData.is_owner
                    : (() => {
                        const ownerCandidates = [
                            playlistData.owner_id,
                            playlistData.user_id,
                            playlistData.created_by,
                        ].map((value) => String(value || "").trim()).filter(Boolean);
                        const currentUserIds = [
                            user?.id,
                            (user as any)?.user_id,
                            (user as any)?.sub,
                            (user as any)?.email,
                        ].map((value) => String(value || "").trim()).filter(Boolean);
                        return ownerCandidates.length > 0
                            ? ownerCandidates.some((ownerId) => currentUserIds.includes(ownerId))
                            : access !== "Public";
                    })(),
                created_at: playlistData.created_at,
                updated_at: playlistData.updated_at,
            };

            console.log("✅ Complete playlist fetched and cached:", {
                id: playlist.id,
                title: playlist.title,
                venueName: playlist.venueName,
                venueId: playlist.venue_id,
                songCount: playlist.songs?.length,
                imageUrl: playlist.image  // ← Now includes ?v=... for fresh load
            });

            // A secondary request (for example, the canonical songs endpoint) may
            // fail while the playlist payload still provides everything needed to
            // render the playlist. Do not leave that recovered failure in the
            // shared, page-level error banner.
            setError(null);
                        return playlist;
                    } finally {
                        setLoadingStates(prev => ({ ...prev, fetchingPlaylist: false }));
                    }
                },
            });
        } catch (error) {
            console.error("❌ Error fetching complete playlist:", error);
            setError(error instanceof Error ? error.message : 'Failed to fetch playlist');
            return null;
        }
    }, [getValidToken, getPlaylistSongs, user, fetchWithRetry, getVenueName, mapSongFromApi, queryClient]);

    const updatePlaylist = useCallback((playlistId: string, updatedPlaylist: Playlist) => {
        setCurrentPlaylistState(prev =>
            prev?.id === playlistId ? updatedPlaylist : prev
        );
        queryClient.setQueryData(playlistKeys.detail(playlistId), updatedPlaylist);

        console.log("🔄 Playlist updated in context:", {
            playlistId,
            title: updatedPlaylist.title,
            songCount: updatedPlaylist.songs?.length
        });
    }, [queryClient]);

    // ✅ ENHANCED: addSongToPlaylist with optimistic updates
    const addSongToPlaylist = useCallback((playlistId: string, song: Song) => {
        const isAddingToOpenPlaylist = Boolean(currentPlaylist?.id && currentPlaylist.id === playlistId);
        if (isAddingToOpenPlaylist && !canModifyPlaylist(currentPlaylist)) {
            console.warn("❌ User cannot modify this playlist");
            throw new Error("You don't have permission to modify this playlist");
        }

        setLoadingStates(prev => ({ ...prev, addingSong: true }));

        console.log("🔄 Context: Adding song to playlist", {
            playlistId,
            songId: song.id,
            songTitle: song.title,
            isOwner: isCurrentPlaylistOwner()
        });

        setAddedSongsMap(prev => {
            const existingSongs = prev[playlistId] || [];
            if (existingSongs.find(s => s.id === song.id)) {
                console.log("⚠️ Song already exists in playlist:", song.id);
                setLoadingStates(prev => ({ ...prev, addingSong: false }));
                return prev;
            }

            const updated = {
                ...prev,
                [playlistId]: [...existingSongs, song]
            };

            debouncedPersist(updated);
            return updated;
        });

        setCurrentPlaylistState(prev => {
            if (!prev || prev.id !== playlistId) return prev;
            const existingSongs = Array.isArray(prev.songs) ? prev.songs : [];
            if (existingSongs.some(existingSong => existingSong.id === song.id)) return prev;
            const nextSongs = [...existingSongs, song];
            return {
                ...prev,
                songs: nextSongs,
                song_count: nextSongs.length,
                songCount: nextSongs.length,
            } as Playlist;
        });

        queryClient.setQueryData<Playlist>(playlistKeys.detail(playlistId), cached => {
            if (!cached) return cached;
            const existingSongs = Array.isArray(cached.songs) ? cached.songs : [];
            if (existingSongs.some(existingSong => existingSong.id === song.id)) return cached;
            const nextSongs = [...existingSongs, song];
            return {
                ...cached,
                songs: nextSongs,
                song_count: nextSongs.length,
                songCount: nextSongs.length,
            } as Playlist;
        });
        queryClient.setQueryData<Song[]>(playlistKeys.songs(playlistId), cachedSongs => {
            if (!cachedSongs || cachedSongs.some(existingSong => existingSong.id === song.id)) {
                return cachedSongs;
            }
            return [...cachedSongs, song];
        });

        window.dispatchEvent(new CustomEvent("playlistSongAdded", {
            detail: { playlistId, song },
        }));
        window.dispatchEvent(new CustomEvent("playlistUpdated", {
            detail: { playlistId, songCountDelta: 1 },
        }));

        setLoadingStates(prev => ({ ...prev, addingSong: false }));
    }, [currentPlaylist, canModifyPlaylist, isCurrentPlaylistOwner, debouncedPersist, queryClient]);

    // ✅ ENHANCED: removeSongFromPlaylist with loading state
    const removeSongFromPlaylist = useCallback((playlistId: string, songId: string) => {
        if (!canModifyPlaylist(currentPlaylist)) {
            console.warn("❌ Only playlist owner/admin can remove songs");
            throw new Error("You don't have permission to remove songs from this playlist");
        }

        setLoadingStates(prev => ({ ...prev, removingSong: true }));

        const normalizedSongId = String(songId || "").trim();
        const withoutRemovedSong = (songs: Song[] = []) => songs.filter((song) => {
            const localId = String(song.id || "").trim();
            const persistedId = String((song as any).song_id || "").trim();
            return localId !== normalizedSongId && persistedId !== normalizedSongId;
        });

        // The playlist itself is the authoritative source used by MusicHome's
        // merge effect. Updating only addedSongsMap lets that effect restore a
        // successfully deleted backend song on the next render.
        setCurrentPlaylistState(prev => {
            if (!prev || prev.id !== playlistId) return prev;
            return { ...prev, songs: withoutRemovedSong(prev.songs || []) };
        });

        // Keep fetchPlaylistWithSongs from returning the pre-delete row until a
        // forced refresh replaces this optimistic cache entry.
        queryClient.setQueryData<Playlist>(playlistKeys.detail(playlistId), cachedPlaylist => {
            if (!cachedPlaylist) return cachedPlaylist;
            return {
                ...cachedPlaylist,
                songs: withoutRemovedSong(cachedPlaylist.songs || []),
            };
        });
        queryClient.setQueryData<Song[]>(playlistKeys.songs(playlistId), cachedSongs =>
            cachedSongs ? withoutRemovedSong(cachedSongs) : cachedSongs
        );

        setAddedSongsMap(prev => {
            const existingSongs = prev[playlistId] || [];
            const updatedSongs = withoutRemovedSong(existingSongs);
            const updated = {
                ...prev,
                [playlistId]: updatedSongs
            };

            debouncedPersist(updated);
            return updated;
        });

        setLoadingStates(prev => ({ ...prev, removingSong: false }));
    }, [currentPlaylist, canModifyPlaylist, debouncedPersist, queryClient]);

    // ✅ ENHANCED: clearPlaylistSongs with loading state
    const clearPlaylistSongs = useCallback((playlistId: string) => {
        if (!canModifyPlaylist(currentPlaylist)) {
            console.warn("❌ Only playlist owner/admin can clear songs");
            throw new Error("You don't have permission to clear this playlist");
        }

        setLoadingStates(prev => ({ ...prev, clearingSongs: true }));

        setAddedSongsMap(prev => {
            const { [playlistId]: _, ...rest } = prev;
            debouncedPersist(rest);
            return rest;
        });

        setLoadingStates(prev => ({ ...prev, clearingSongs: false }));
    }, [currentPlaylist, canModifyPlaylist, debouncedPersist]);

    // ✅ ENHANCED: playPlaylist with better error handling
    const playPlaylist = useCallback(async (playlist: Playlist) => {
        console.log("🎵 Playing playlist:", playlist.title);
        console.log("👤 Current user:", user?.id);

        if (!canViewPlaylist(playlist)) {
            console.warn("❌ User cannot view this playlist");
            setError("You don't have permission to view this playlist");
            throw new Error("You don't have permission to view this playlist");
        }

        const updatedPlaylist = await fetchPlaylistWithSongs(playlist.id, true);
        if (updatedPlaylist) {
            const refreshedPlaylist = {
                ...updatedPlaylist,
                owner_id: updatedPlaylist.owner_id || playlist.owner_id,
                user_id: updatedPlaylist.user_id || playlist.user_id,
                created_by: updatedPlaylist.created_by || playlist.created_by,
                is_owner: typeof updatedPlaylist.is_owner === "boolean"
                    ? updatedPlaylist.is_owner
                    : playlist.is_owner,
            };
            setCurrentPlaylist(refreshedPlaylist);
            console.log("✅ Playlist refreshed with songs:", refreshedPlaylist.songs?.length);
            setError(null);
            return refreshedPlaylist;
        }

        console.warn("⚠️ Fresh playlist fetch failed; opening the available playlist data");
        setCurrentPlaylist(playlist);
        return playlist;
    }, [fetchPlaylistWithSongs, user, canViewPlaylist]);

    // ✅ ENHANCED: refreshCurrentPlaylist with error handling
    const refreshCurrentPlaylist = useCallback(async () => {
        if (currentPlaylist) {
            console.log('🔄 Refreshing playlist:', currentPlaylist.id);
            try {
                const refreshedPlaylist = await fetchPlaylistWithSongs(currentPlaylist.id, true);
                if (refreshedPlaylist) {
                    setCurrentPlaylist(refreshedPlaylist);
                    console.log("✅ Playlist refreshed:", refreshedPlaylist.songs?.length);
                    setError(null);
                }
            } catch (error) {
                console.error("❌ Failed to refresh playlist:", error);
                setError("Failed to refresh playlist");
                throw error;
            }
        }
    }, [currentPlaylist, fetchPlaylistWithSongs]);

    // ✅ ADDED: Cache invalidation function
    const invalidateCache = useCallback((playlistId?: string) => {
        if (playlistId) {
            void queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
            setVenueCache(prev => {
                const { [playlistId]: _, ...rest } = prev;
                return rest;
            });
        } else {
            void queryClient.invalidateQueries({ queryKey: playlistKeys.all });
            setVenueCache({});
            console.log("🗑️ Cleared all caches");
        }
    }, [queryClient]);

    // ✅ ADDED: Clear error function
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Reset user-scoped state when authenticated user changes.
    useEffect(() => {
        manualSessionOverrideIdRef.current = null;
        setCurrentPlaylistState(null);
        setAddedSongsMap({});

        // Cleanup legacy unscoped keys to avoid cross-user leakage.
        localStorage.removeItem("currentPlaylist");
        localStorage.removeItem("playlist_added_songs");
    }, [userStorageId]);

    // Load persisted added songs for the active user.
    useEffect(() => {
        if (!addedSongsStorageKey) return;
        try {
            const raw = localStorage.getItem(addedSongsStorageKey);
            if (raw) {
                const persisted = JSON.parse(raw);
                console.log("📥 Context: Loaded persisted songs:", Object.keys(persisted).length, "playlists");
                setAddedSongsMap(persisted);
            }
        } catch (error) {
            console.error('Failed to load persisted songs:', error);
        }
    }, [addedSongsStorageKey]);

    // ✅ ADDED: Cross-tab synchronization
    useEffect(() => {
        if (!addedSongsStorageKey && !currentPlaylistStorageKey) return;

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === addedSongsStorageKey && e.newValue) {
                try {
                    const updatedSongs = JSON.parse(e.newValue);
                    console.log("🔄 Cross-tab sync: Added songs updated");
                    setAddedSongsMap(updatedSongs);
                } catch (error) {
                    console.error('Failed to parse storage update:', error);
                }
            }

            if (e.key === currentPlaylistStorageKey && e.newValue) {
                try {
                    const updatedPlaylist = JSON.parse(e.newValue);
                    if (updatedPlaylist?.id && !updatedPlaylist.id.startsWith("mock_")) {
                        console.log("🔄 Cross-tab sync: Current playlist updated");
                        applyCurrentPlaylistSelection(updatedPlaylist, "restore");
                    }
                } catch (error) {
                    console.error('Failed to parse playlist storage update:', error);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [addedSongsStorageKey, applyCurrentPlaylistSelection, currentPlaylistStorageKey]);

    // Debug: Log when addedSongsMap changes
    useEffect(() => {
        console.log("🔄 CurrentPlaylistContext State:", {
            currentPlaylist: currentPlaylist?.title,
            currentPlaylistId: currentPlaylist?.id,
            currentUserId: user?.id,
            isCurrentPlaylistOwner: isCurrentPlaylistOwner(),
            addedSongsMap: Object.keys(addedSongsMap).reduce((acc, key) => {
                acc[key] = addedSongsMap[key].length;
                return acc;
            }, {} as Record<string, number>),
            isFetchingSongs
        });
    }, [currentPlaylist, addedSongsMap, isFetchingSongs, user, isCurrentPlaylistOwner]);

    // ✅ SESSION → CURRENT PLAYLIST SYNC (CRITICAL)
    useEffect(() => {
        const handleSessionPlaylistSync = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { playlist, sessionId: syncedSessionId } = customEvent.detail || {};

            if (!playlist || !playlist.id) {
                console.warn("⚠️ sessionPlaylistSync received invalid playlist");
                return;
            }

            const incomingSessionId = String(syncedSessionId || normalizedSessionId).trim();
            const incomingPlaylistId = String(playlist.id || "").trim();
            const openPlaylistId = String(currentPlaylistRef.current?.id || "").trim();
            const isManualOverrideForActiveSession = Boolean(
                manualSessionOverrideIdRef.current &&
                incomingSessionId &&
                manualSessionOverrideIdRef.current === incomingSessionId &&
                openPlaylistId &&
                openPlaylistId !== incomingPlaylistId
            );

            if (isManualOverrideForActiveSession) {
                console.log("⏸️ Ignoring session playlist sync while another playlist is open:", {
                    incomingSessionId,
                    incomingPlaylistId,
                    openPlaylistId,
                });
                return;
            }

            console.log("🔗 Syncing session playlist into CurrentPlaylistContext:", {
                playlistId: playlist.id,
                title: playlist.title,
                songCount: playlist.songs?.length
            });

            applyCurrentPlaylistSelection(playlist, "session");
        };

        window.addEventListener("sessionPlaylistSync", handleSessionPlaylistSync);

        return () => {
            window.removeEventListener("sessionPlaylistSync", handleSessionPlaylistSync);
        };
    }, [applyCurrentPlaylistSelection, normalizedSessionId]);


    // Persist current playlist on change
    useEffect(() => {
        if (!currentPlaylistStorageKey) return;

        if (currentPlaylist) {
            localStorage.setItem(currentPlaylistStorageKey, JSON.stringify(currentPlaylist));
        } else {
            localStorage.removeItem(currentPlaylistStorageKey);
        }
    }, [currentPlaylist, currentPlaylistStorageKey]);

    // Restore persisted playlist for the active user only.
    useEffect(() => {
        if (!currentPlaylistStorageKey) return;

        const stored = localStorage.getItem(currentPlaylistStorageKey);
        if (stored && !currentPlaylist) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed?.id && !parsed.id.startsWith("mock_")) {
                    applyCurrentPlaylistSelection(parsed, "restore");
                    console.log("📥 Restored real currentPlaylist:", parsed.id);
                } else {
                    localStorage.removeItem(currentPlaylistStorageKey);
                }
            } catch (err) {
                console.warn("⚠️ Failed to parse stored currentPlaylist:", err);
                localStorage.removeItem(currentPlaylistStorageKey);
            }
        }
    }, [applyCurrentPlaylistSelection, currentPlaylist, currentPlaylistStorageKey]);

    const value: CurrentPlaylistContextType = {
        currentPlaylist,
        setCurrentPlaylist,
        addedSongsMap,
        addSongToPlaylist,
        removeSongFromPlaylist,
        clearPlaylistSongs,
        playPlaylist,
        refreshCurrentPlaylist,
        isPlaylistExpanded,
        setIsPlaylistExpanded,
        isAddingSong,
        setIsAddingSong,
        getPlaylistSongs,
        updatePlaylist,
        isFetchingSongs,
        fetchPlaylistWithSongs,
        isPlaylistOwner,
        isCurrentPlaylistOwner,
        canModifyPlaylist,
        canViewPlaylist,
        // ✅ ADDED: New improved properties
        error,
        clearError,
        loadingStates,
        invalidateCache,
        fetchVenueDetails,
        getVenueName,
        prefetchVenues, // ✅ ADDED: Bulk venue pre-fetching
    };

    return (
        <CurrentPlaylistContext.Provider value={value}>
            {children}
        </CurrentPlaylistContext.Provider>
    );
};

export const useCurrentPlaylist = (): CurrentPlaylistContextType => {
    const context = useContext(CurrentPlaylistContext);
    if (context === undefined) {
        throw new Error('useCurrentPlaylist must be used within a CurrentPlaylistProvider');
    }
    return context;
};
